(ns ncr.cube
  (:require
   [clojure.set :as set]
   [com.rpl.specter :as sp]
   [clojure.math.combinatorics :as combo]
   [taoensso.encore :as e]))

(defn- group-map [values]
  (cond
    (empty? values)
    {::emptiness #{}}

    (sequential? values)
    (into {} (map #(do [% #{}]) values))

    (map? values)
    (apply merge-with into {}
      (map (fn [[k v]] (e/map-vals #(conj % k) (group-map v)))
        values))))

#_(group-map [1 2 3 4])
#_(group-map {:group [1 2 3 4]})
#_(group-map {:globs {}})
#_(group-map
    {:globs
     {["ntt*"] ["label1" "label2" "label3"]
      ["empty*"] []
      ["daimler*"] ["label4" "label2" "label5"]}})

(defn- prepare-axis [{:keys [total other] :as axis}]
  (let [group-map (group-map (:values axis))
        total (when total (if (keyword? total) total :total))
        other (when other (if (keyword? other) other :other))
        groups
        (cond-> (->> group-map vals (apply concat) set)
          total (conj total)
          other (conj other))
        group-map (dissoc group-map ::emptiness)]
    (assoc axis
      :group-map group-map :groups groups :total total :other other)))

#_(prepare-axis {:values {:group [1 2 3]}})
#_(prepare-axis {:values {:group []}})
#_(prepare-axis {:values {:group [1 2 3]} :other true :total :all})
#_(prepare-axis {:values {:total {["*"] ["a" "b"]}}})
#_(prepare-axis {:values {:total {["*"] []}}})
#_(prepare-axis {:values {:total {}}})

(defn create [{:keys [axes zero ops] :as cube}]
  (let [axes (mapv prepare-axis axes)
        ops (cond->> ops zero (e/map-vals #(fnil % zero)))]
    (assoc cube :axes axes :ops ops :data nil)))

(defn- key-map->vec [{:keys [axes] :as cube} mp]
  (mapv
    #(val (e/have! (find mp (:name %)) :data {:axes % :k mp}))
    axes))

(defn- expand-key [{:keys [multiple group-map other total] :as axes} vs]
  (let [vs (if-not multiple [vs] vs)
        in (->> vs
             (keep #(when-let [gm (group-map %)] (conj gm %)))
             (apply set/union))
        ks (if (and (not (seq in)) other) #{other} in)]
    (cond-> ks
      total (conj total))))

(defn- valid-key? [axes ak]
  (some #(contains? (% axes) ak) [:group-map :groups]))

(defn op [{:keys [ops axes data] :as cube} opf k v]
  (let [opf (e/have! (cond->> opf (keyword? opf) ops))
        k (cond->> k (map? k) (key-map->vec cube))
        ks (mapv #(expand-key %1 %2) axes k)]
    (assoc cube :data
      (reduce
        #(update-in %1 %2 opf v)
        data
        (apply combo/cartesian-product ks)))))

(defn update-cells [{:keys [axes data zero] :as cube} k f & args]
  (let [k (cond->> k (map? k) (key-map->vec cube))
        f (cond-> f zero (fnil zero))
        af #(apply f % args)
        ks (mapv #(expand-key %1 %2) axes k)]
    (assoc cube :data
      (reduce
        #(update-in %1 %2 af)
        data
        (apply combo/cartesian-product ks)))))

(defn update-cell [{:keys [zero] :as cube} k f & args]
  (let [k (cond->> k (map? k) (key-map->vec cube))
        f (cond-> f zero (fnil zero))
        af #(apply f % args)]
    (update cube :data update-in k af)))

(defn cell
  ([cube k] (cell cube k nil))
  ([{:keys [axes data] :as cube} k not-found]
   (let [not-found (if (true? not-found) (:zero cube) not-found)
         k (cond->> k (map? k) (key-map->vec cube))]
     (dotimes [i (count axes)]
       (assert (valid-key? (axes i) (k i))))
     (get-in data k not-found))))

(defn slice [{:keys [axes data] :as cube} pk]
  (e/have! map? pk)
  (loop [axes' []
         [{aname :name :as axis} & axes] axes
         data data]
    (if-not axis
      (assoc cube :axes axes' :data data)
      (if-let [[_ v] (find pk aname)]
        (let [path (repeat (count axes') sp/MAP-VALS)
              data (sp/transform path #(get % v) data)]
          (assert (valid-key? axis v))
          (recur axes' axes data))
        (recur (conj axes' axis) axes data)))))

(defn map-vals [f {:keys [axes data] :as cube}]
  (let [path (into [:data] (repeat (count axes) sp/MAP-VALS))]
    (sp/transform path f cube)))

#_(map-vals inc {:axes [1] :data {:a 1 :b 2}})

(defn vals-map [cube f]
  (map-vals f cube))

(defn strip [cube]
  (dissoc cube :ops))

(defn axes-domain [{:keys [axes] :as cube} i]
  (let [{:keys [group-map groups]} (e/have! (axes i))]
    (into groups (keys group-map))))

(comment
  (require '[clojure.pprint :refer [pprint]])

  (def ^:private d1
    {:axes
     [{:name :labels
       :multiple true
       :total true
       :other true
       :values
       {:globs
        {["ntt*"] ["label1" "label2" "label3"]
         ["daimler*"] ["label4" "label2" "label5"]}}}
      {:name :domain
       :values [:actual :estimated]}
      {:name :month
       :values (vec (range 1 13))
       :total :total}]

     :zero [0M 0]
     :ops {:+ #(mapv + %1 %2)
           :- (fn [a b] (mapv #(%1 %2 %3) [- +] a b))}})

  (def ^:private g1 (create d1))

  (axes-domain g1 0)

  (key-map->vec g1 {:labels "label1" :domain :actual :month 6})

  (expand-key (get-in g1 [:axes 0]) ["label1"])
  (expand-key (get-in g1 [:axes 1]) :actual)
  (expand-key (get-in g1 [:axes 2]) 3)

  (cell
    (op g1 :+ {:labels ["label1"] :domain :actual :month 2} [1.00M 1])
    [["ntt*"] :actual :total])

  (def ^:private g2
    (reduce
      #(apply op %1 :+ %2)
      g1
      [[{:labels ["label1"] :month 2 :domain :actual} [1M 1]]
       [{:labels ["label2"] :month 3 :domain :actual} [2M 1]]
       [{:labels ["label3"] :month 3 :domain :actual} [3M 1]]
       [{:labels ["label4"] :month 2 :domain :estimated} [4M 1]]
       [{:labels ["label6"] :month 2 :domain :estimated} [4M 1]]]))

  (pprint (slice g2 {:labels :total}))
  (pprint (slice g2 {:month :total}))
  ;;
  )
